import { flow, isNull, set } from 'lodash/fp';
import { Reducer } from 'redux';

import { LoginActionTypes } from '../../../../auth/login/store/types';
import { DEFAULT_PAGE_SIZE } from '../../../../constants/arkTable';
import { AnalysisView } from '../../../../documents/store/types';
import { FewShotDocumentActionTypes, FewShotDocumentsState, FewShotDocumentUploadInfo } from './types';

export const INITIAL_STATE: FewShotDocumentsState = {
    selectedAgreementTypeId: null,
    isFetchingDocuments: false,
    documents: [],
    documentsTotal: 0,
    tableFilters: {},
    tablePageNumber: 1,
    tableColumnSort: undefined,
    tablePageSize: DEFAULT_PAGE_SIZE,
    uploadModalOpen: false,
    isUploading: false,
    filesToUpload: null,
    fileInformation: [],
    isOpening: false,
    documentUrl: null,
    document: null,
    savedDocument: null,
    queries: [],
    analysisView: AnalysisView.SPLIT,
    selectedPage: null,
    isUpdatingFewShotAnswers: false,
    documentToDelete: null,
    isDeleting: false,
    savedPriorityOrder: {},
    currentPriorityOrder: {},
    priorityModalOpen: false,
    isUpdatingPriority: false
};

export const fewShotDocumentsReducer: Reducer<FewShotDocumentsState> = (state = INITIAL_STATE, { payload, type }): FewShotDocumentsState => {
    switch (type) {
        case FewShotDocumentActionTypes.SET_SELECTED_FEW_SHOT_AGREEMENT_TYPE: {
            const { agreementTypeId } = payload;
            if (!isNull(payload)) {
                return set('selectedAgreementTypeId', agreementTypeId, state);
            }
            return INITIAL_STATE;
        }
        case FewShotDocumentActionTypes.FETCH_AGREEMENT_TYPE_FEW_SHOT_DOCUMENTS_STARTED:
            return flow(
                set('isFetchingDocuments', true),
                set('tablePageNumber', payload)
            )(state);
        case FewShotDocumentActionTypes.FETCH_AGREEMENT_TYPE_FEW_SHOT_DOCUMENTS_SUCCESSFUL: {
            const { documents, total } = payload;
            return flow(
                set('isFetchingDocuments', false),
                set('documents', documents),
                set('documentsTotal', total)
            )(state);
        }
        case FewShotDocumentActionTypes.FETCH_AGREEMENT_TYPE_FEW_SHOT_DOCUMENTS_FAILED:
            return set('isFetchingDocuments', false, state);
        case FewShotDocumentActionTypes.SET_FEW_SHOT_DOCUMENTS_TABLE_FILTERS: {
            const isTextFilter = payload.type === 'text';
            return flow(
                set(`tableFilters[${payload.key}][${payload.type}]`, payload.value),
                set(`tableFilters[${payload.key}][${isTextFilter ? 'dropdown' : 'text'}]`, isTextFilter ? null : '')
            )(state);
        }
        case FewShotDocumentActionTypes.CLEAR_FEW_SHOT_DOCUMENTS_TABLE_FILTERS:
            return set('tableFilters', {}, state);
        case FewShotDocumentActionTypes.SET_FEW_SHOT_DOCUMENTS_TABLE_COLUMN_SORT:
            return set('tableColumnSort', payload, state);
        case FewShotDocumentActionTypes.SET_FEW_SHOT_DOCUMENTS_PAGE_SIZE:
            return set('tablePageSize', payload, state);
        case FewShotDocumentActionTypes.TOGGLE_UPLOAD_FEW_SHOT_DOCUMENT_MODAL_OPEN:
            return flow(
                set('uploadModalOpen', payload),
                set('filesToUpload', null),
                set('fileInformation', [])
            )(state);
        case FewShotDocumentActionTypes.OPEN_FEW_SHOT_DOCUMENT_STARTED:
            return set('isOpening', true, state);
        case FewShotDocumentActionTypes.OPEN_FEW_SHOT_DOCUMENT_SUCCESSFUL: {
            const { documentUrl, document, queries } = payload;
            return flow(
                set('isOpening', false),
                set('documentUrl', documentUrl),
                set('document', document),
                set('savedDocument', document),
                set('queries', queries)
            )(state);
        }
        case FewShotDocumentActionTypes.OPEN_FEW_SHOT_DOCUMENT_FAILED:
            return set('isOpening', false, state);
        case FewShotDocumentActionTypes.TOGGLE_FEW_SHOT_DOCUMENT_ANALYSIS_VIEW:
            return set('analysisView', payload, state);
        case FewShotDocumentActionTypes.RESET_FEW_SHOT_DOCUMENT:
            return flow(
                set('documentUrl', null),
                set('document', null),
                set('queries', [])
            )(state);
        case FewShotDocumentActionTypes.UPLOAD_FEW_SHOT_DOCUMENTS_STARTED:
            return set('isUploading', true, state);
        case FewShotDocumentActionTypes.UPLOAD_FEW_SHOT_DOCUMENTS_SUCCESSFUL:
            return flow(
                set('isUploading', false),
                set('uploadModalOpen', false),
                set('filesToUpload', null)
            )(state);
        case FewShotDocumentActionTypes.UPLOAD_FEW_SHOT_DOCUMENTS_FAILED:
            return set('isUploading', false, state);
        case FewShotDocumentActionTypes.SET_INITIAL_FEW_SHOT_DOCUMENTS: {
            const fileInformation: FewShotDocumentUploadInfo[] = (payload as File[]).map(({ name }) => ({ documentDescription: name, documentNameId: null, clientId: null, isSystem: false, scheduleStartPage: null, scheduleEndPage: null }));
            return flow(
                set('filesToUpload', payload),
                set('fileInformation', fileInformation)
            )(state);
        }
        case FewShotDocumentActionTypes.UPDATE_FEW_SHOT_DOCUMENT_DETAILS: {
            const { value, key, index } = payload;
            return set(`fileInformation[${index}][${key}]`, value, state);
        }
        case FewShotDocumentActionTypes.SET_FEW_SHOT_DOCUMENT_SELECTED_PAGE:
            return set('selectedPage', payload, state);
        case FewShotDocumentActionTypes.SET_FEW_SHOT_DOCUMENT_AI_ANSWER_HIGHLIGHT_INDEXES: {
            const { key, indexes, isFuzzyMatchHighlight } = payload;
            if (state.document && state.document.fewShotAnswers) {
                const updatedFewShotAnswers = Object.entries(state.document.fewShotAnswers).reduce((acc, [queryKey, mlData]) => {
                    if (queryKey === key) {
                        return set(queryKey, { ...mlData, highlightIndexes: indexes, isFuzzyMatchHighlight }, acc);
                    }
                    return set(queryKey, mlData, acc);
                }, {});
                return set('document.fewShotAnswers', updatedFewShotAnswers, state);
            }
            return state;
        }
        case FewShotDocumentActionTypes.SET_FEW_SHOT_DOCUMENT_ANSWER: {
            const { key, value } = payload;
            if (state.document && state.document.fewShotAnswers) {
                const updatedFewShotAnswers = Object.entries(state.document.fewShotAnswers).reduce((acc, [queryKey, mlData]) => {
                    if (queryKey === key) {
                        return set(queryKey, { ...mlData, value }, acc);
                    }
                    return set(queryKey, mlData, acc);
                }, {});
                return set('document.fewShotAnswers', updatedFewShotAnswers, state);
            }
            return state;
        }
        case FewShotDocumentActionTypes.UPDATE_FEW_SHOT_ANSWERS_STARTED:
            return set('isUpdatingFewShotAnswers', true, state);
        case FewShotDocumentActionTypes.UPDATE_FEW_SHOT_ANSWERS_SUCCESSFUL:
            return flow(
                set('isUpdatingFewShotAnswers', false),
                set('savedDocument', state.document)
            )(state);
        case FewShotDocumentActionTypes.UPDATE_FEW_SHOT_ANSWERS_FAILED:
            return set('isUpdatingFewShotAnswers', false, state);
        case FewShotDocumentActionTypes.TOGGLE_FEW_SHOT_DOCUMENT_DELETE_MODAL:
            return set('documentToDelete', payload, state);
        case FewShotDocumentActionTypes.DELETE_FEW_SHOT_DOCUMENT_STARTED:
            return set('isDeleting', true, state);
        case FewShotDocumentActionTypes.DELETE_FEW_SHOT_DOCUMENT_SUCCESSFUL:
            return flow(
                set('isDeleting', false),
                set('documentToDelete', null)
            )(state);
        case FewShotDocumentActionTypes.DELETE_FEW_SHOT_DOCUMENT_FAILED:
            return set('isDeleting', false, state);
        case FewShotDocumentActionTypes.TOGGLE_FEW_SHOT_PRIORITY_MODAL_OPEN:
            return flow(
                set('priorityModalOpen', payload),
                set('currentPriorityOrder', state.savedPriorityOrder)
            )(state);
        case FewShotDocumentActionTypes.UPDATE_FEW_SHOT_DOCUMENTS_PRIORITY_ORDER:
            return flow(
                set('currentPriorityOrder', payload.priorityOrder),
                set('savedPriorityOrder', payload.setInitial ? payload.priorityOrder : state.savedPriorityOrder)
            )(state);
        case FewShotDocumentActionTypes.UPDATE_FEW_SHOT_PRIORITY_ORDER_STARTED:
            return set('isUpdatingPriority', true, state);
        case FewShotDocumentActionTypes.UPDATE_FEW_SHOT_PRIORITY_ORDER_SUCCESSFUL:
            return flow(
                set('isUpdatingPriority', false),
                set('savedPriorityOrder', state.currentPriorityOrder)
            )(state);
        case FewShotDocumentActionTypes.UPDATE_FEW_SHOT_PRIORITY_ORDER_FAILED:
            return set('isUpdatingPriority', false, state);
        case LoginActionTypes.LOGOUT_SUCCESSFUL:
            return INITIAL_STATE;
        default:
            return state;
    }
};
